import { config } from './config';
import type { ApiPath, ResponseTypeMap, ApiError, API_PATHS } from 'src/types/api';
import { getSessionId, getSessionURL } from 'src/plugins/openreplay';
const unknownError = 'Unknown error happened check your api-key and config';
export function getBaseUrl(path: ApiPath) {
  return `${env.VITE_APP_API_BASE_URL}${config.value.clientId}/${path}`;
}

export async function makeFetch(input: RequestInfo | URL, init?: RequestInit | undefined) {
  try {
    const sessionId = getSessionId();
    const sessionUrl = getSessionURL();
    const res = await fetch(input, {
      ...init,
      headers: {
        'X-API-KEY': config.value.apiKey,
        Authorization: `Bearer ${config.value.authToken}`,
        'Content-Type': 'application/json',
        ...(sessionId ? { 'openReplaySession.id': sessionId || '' } : {}),
        ...(sessionUrl ? { openReplaySessionURL: sessionUrl || '' } : {}),
        ...((init?.headers ? init?.headers : {}) as Record<string, string>),
      },
    }).then(async (res) => {
      const isJson = res.headers.get('content-type') === 'application/json';
      if (!res.ok) {
        throw await res.json();
      }
      return isJson ? res.json() : res.text();
    });
    return [res, null] as const;
  } catch (error) {
    console.log(`output->error`, error);
    if (
      error &&
      typeof error === 'object' &&
      'statusMessage' in error &&
      typeof error?.statusMessage === 'string' &&
      !!error?.statusMessage
    ) {
      return [null, { message: error.statusMessage }] as const;
    }
    return [null, { message: unknownError }] as const;
  }
}

async function get<Endpoint extends keyof typeof API_PATHS>(
  endpoint: Endpoint,
  payload?: { params?: string; query?: Record<string, string> }
) {
  const url = new URL(`${getBaseUrl(endpoint)}${payload?.params ? `/${payload.params}` : ''}`);
  if (payload?.query) {
    Object.entries(payload.query).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        url.searchParams.append(key, value);
      }
    });
  }
  return makeFetch(url.toString(), {
    method: 'GET',
  }) as Promise<[ResponseTypeMap[Endpoint], null] | [null, ApiError]>;
}

async function post<Body, Endpoint extends keyof typeof API_PATHS>(endpoint: Endpoint, body?: Body) {
  return makeFetch(getBaseUrl(endpoint), {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<[ResponseTypeMap[Endpoint], null] | [null, ApiError]>;
}

async function put<Body, Endpoint extends keyof typeof API_PATHS>(endpoint: Endpoint, userId: string, body?: Body) {
  return makeFetch(`${getBaseUrl(endpoint)}/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as Promise<[ResponseTypeMap[Endpoint], null] | [null, ApiError]>;
}

async function del<Body, Endpoint extends keyof typeof API_PATHS>(endpoint: Endpoint, userId: string, body?: Body) {
  return makeFetch(`${getBaseUrl(endpoint)}`, {
    method: 'DELETE',
    body: JSON.stringify(body),
  }) as Promise<[ResponseTypeMap[Endpoint], null] | [null, ApiError]>;
}

async function validate<Body>(body?: Body) {
  return makeFetch(getBaseUrl('config'), {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<[ResponseTypeMap['config'], null] | [null, ApiError]>;
}

export const request = {
  get,
  post,
  put,
  delete: del,
  validate,
};
